import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

export const READER_FONT_FAMILIES = [
  {
    id: "default",
    nameZh: "系统默认",
    nameEn: "System",
    previewZh: "起初神创造天地",
    previewEn: "In the beginning",
    css: 'system-ui, -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif',
  },
  {
    id: "song",
    nameZh: "宋体",
    nameEn: "Serif (Song)",
    previewZh: "起初神创造天地",
    previewEn: "In the beginning",
    css: '"Songti SC", "STSong", "SimSun", "Noto Serif CJK SC", Georgia, serif',
  },
  {
    id: "kai",
    nameZh: "楷体",
    nameEn: "Kai",
    previewZh: "起初神创造天地",
    previewEn: "In the beginning",
    css: '"Kaiti SC", "STKaiti", "KaiTi", "BiauKai", Georgia, serif',
  },
  {
    id: "hei",
    nameZh: "黑体",
    nameEn: "Sans (Hei)",
    previewZh: "起初神创造天地",
    previewEn: "In the beginning",
    css: '"Heiti SC", "STHeiti", "SimHei", "Noto Sans CJK SC", Arial, sans-serif',
  },
];

export const READER_LINE_HEIGHTS = [
  { id: "compact", value: 1.6, nameZh: "紧凑 1.6", nameEn: "Compact 1.6" },
  { id: "normal", value: 1.8, nameZh: "标准 1.8", nameEn: "Normal 1.8" },
  { id: "relaxed", value: 2.1, nameZh: "宽松 2.1", nameEn: "Relaxed 2.1" },
];

export const READER_WIDTH_PRESETS = [
  { id: "narrow", value: 60, nameZh: "窄 (60%)", nameEn: "Narrow", shortZh: "窄" },
  { id: "medium", value: 75, nameZh: "适中 (75%)", nameEn: "Medium", shortZh: "适中" },
  { id: "wide", value: 90, nameZh: "宽 (90%)", nameEn: "Wide", shortZh: "宽" },
  { id: "full", value: 100, nameZh: "全宽 (100%)", nameEn: "Full", shortZh: "全宽" },
];

const STORAGE_FONT_FAMILY = "bible_reader_font_family";
const STORAGE_LINE_HEIGHT = "bible_reader_line_height";

const getInitialFontFamilyId = () => {
  try {
    const saved = localStorage.getItem(STORAGE_FONT_FAMILY);
    if (saved && READER_FONT_FAMILIES.some((f) => f.id === saved)) {
      return saved;
    }
  } catch (e) {
    // ignore storage error
  }
  return "default";
};

const getInitialLineHeight = () => {
  try {
    const saved = parseFloat(localStorage.getItem(STORAGE_LINE_HEIGHT));
    if (Number.isFinite(saved) && saved >= 1.2 && saved <= 3.0) {
      return saved;
    }
  } catch (e) {
    // ignore storage error
  }
  return 1.8;
};

const DEFAULT_CONTEXT = {
  fontFamilyId: "default",
  fontFamily: READER_FONT_FAMILIES[0].css,
  lineHeight: 1.8,
  setFontFamilyId: () => {},
  setLineHeight: () => {},
};

const ReaderTypographyContext = createContext(DEFAULT_CONTEXT);

export const ReaderTypographyProvider = ({ children }) => {
  const [fontFamilyId, setFontFamilyIdState] = useState(getInitialFontFamilyId);
  const [lineHeight, setLineHeightState] = useState(getInitialLineHeight);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_FONT_FAMILY, fontFamilyId);
    } catch (e) {
      // ignore storage error
    }
  }, [fontFamilyId]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_LINE_HEIGHT, String(lineHeight));
    } catch (e) {
      // ignore storage error
    }
  }, [lineHeight]);

  const setFontFamilyId = (nextId) => {
    if (READER_FONT_FAMILIES.some((f) => f.id === nextId)) {
      setFontFamilyIdState(nextId);
    }
  };

  const setLineHeight = (nextVal) => {
    const num = Number(nextVal);
    if (Number.isFinite(num) && num >= 1.2 && num <= 3.0) {
      setLineHeightState(num);
    }
  };

  const fontFamily = useMemo(() => {
    const found = READER_FONT_FAMILIES.find((f) => f.id === fontFamilyId);
    return found ? found.css : READER_FONT_FAMILIES[0].css;
  }, [fontFamilyId]);

  const value = useMemo(
    () => ({
      fontFamilyId,
      fontFamily,
      lineHeight,
      setFontFamilyId,
      setLineHeight,
    }),
    [fontFamily, fontFamilyId, lineHeight]
  );

  return (
    <ReaderTypographyContext.Provider value={value}>
      {children}
    </ReaderTypographyContext.Provider>
  );
};

export const useReaderTypography = () => {
  return useContext(ReaderTypographyContext) || DEFAULT_CONTEXT;
};

export default ReaderTypographyContext;
